import { AgGridPageRequest, AgGridSortModel, InstrumentValuesGridResponse, InstrumentValuesReportViewModel, PortfolioReportClient } from '@dynamik/common/clients/reporting-clients';
import { CustomerService, enumToExtendedArray, IApplicationFilter, PortfolioReportCache, property, widget, WidgetPropertyType } from '@dynamik/common';
import { BindingEngine } from 'aurelia-binding';
import { WidgetBase } from '@dynamik/common';
import { Filter } from '../../../../types/filter';
import { customElement } from 'aurelia-templating';
import { portfolioInstrumentFields } from '../portfolio-instrument-fields';

export enum TopBottomInstrumentsType {
  Top = 1,
  Bottom = 2
}

export enum TopBottomInstrumentsWidgetStartFrom {
  ToDateYearStart = 1,
  FromDate = 2
}

export enum TopBottomNameField {
  Name = 'name',
  ReportName = 'reportName'
}

declare module '@dynamik/common/clients/reporting-clients' {
  interface IInstrumentValuesReportViewModel {
    estimatedValuePercent?: number;
  }
  interface InstrumentValuesReportViewModel extends IInstrumentValuesReportViewModel {
    estimatedValuePercent?: number;
  }
}

@customElement('top-bottom-instruments-light-widget')
@widget('Top/Bottom instruments (light)', true, false, 'Description')
export class TopBottomInstrumentsLightWidget extends WidgetBase {
  constructor(element: Element, customerService: CustomerService, private portfolioReportClient: PortfolioReportClient, bindingEngine: BindingEngine) {
    super(element, bindingEngine, customerService);
  }

  fieldLabel: string;
  total: InstrumentValuesReportViewModel;
  instruments: InstrumentValuesReportViewModel[];
  TopBottomInstrumentsWidgetStartFrom = TopBottomInstrumentsWidgetStartFrom;

  @property('Data', undefined, WidgetPropertyType.List, [
    { value: TopBottomInstrumentsWidgetStartFrom.ToDateYearStart, label: '"To" date year start' },
    { value: TopBottomInstrumentsWidgetStartFrom.FromDate, label: '"From" date' }
  ])
  startFrom: TopBottomInstrumentsWidgetStartFrom = TopBottomInstrumentsWidgetStartFrom.ToDateYearStart;

  @property('Data', undefined, WidgetPropertyType.List, enumToExtendedArray(TopBottomInstrumentsType))
  type: TopBottomInstrumentsType = TopBottomInstrumentsType.Top;

  @property('Data', undefined, WidgetPropertyType.List, [{ value: TopBottomNameField.Name, label: 'Name' }, { value: TopBottomNameField.ReportName, label: 'Report name' }])
  nameField: TopBottomNameField = TopBottomNameField.Name;

  @property('Data', undefined, WidgetPropertyType.List, portfolioInstrumentFields.map(x => ({ value: x.name, label: x.label })))
  field: Filter<InstrumentValuesReportViewModel, number> = 'profitLossChangeYearToDate';

  @property('Data')
  descending: boolean = true;

  @property('Data')
  count: number = 10;

  @property('Data')
  precision?: number = 2;

  @property('Table')
  font?: string;

  @property('Table')
  fontSize?: number = 14;

  @property('Table')
  color?: string;

  @property('Table')
  rowHeight?: number;

  @property()
  captionFont?: string;

  @property()
  captionFontSize?: number = 14;

  @property()
  captionColor?: string;

  protected async loadData(filter: IApplicationFilter) {
    return this.portfolioReportClient.instrumentValuesGrid(filter.customer.companyId, filter.dates.fromStr, filter.dates.toStr, undefined, undefined, 0,
      new AgGridPageRequest({
        startRow: 0,
        endRow: this.count,
        sortModel: [new AgGridSortModel({ colId: this.field, sort: this.type === TopBottomInstrumentsType.Top ? 'desc' : 'asc' })]
      })
    );
  }

  protected async setData(data: InstrumentValuesGridResponse): Promise<void> {
    this.fieldLabel = portfolioInstrumentFields.find(x => x.name === this.field)?.label;
    this.instruments = data.values;
    this.instruments.sort((a, b) => (this.descending ? 1 : -1) * (b[this.field] - a[this.field]))
    this.total = this.instruments.reduce((acc, cur) => {
      acc.estimatedValueClosingBalance += cur.estimatedValueClosingBalance;
      acc.profitLossChangeYearToDate += cur.profitLossChangeYearToDate;
      return acc;
    }, new InstrumentValuesReportViewModel({ name: 'TOTAL', estimatedValueClosingBalance: 0, profitLossChangeYearToDate: 0 }));
     this.instruments.forEach(y => {
       y.estimatedValuePercent = y.estimatedValueClosingBalance / data.total * 100;
     });

     this.instruments.push(this.total);
     this.excelData = {
       fileName: 'Top Bottom Instruments',
       data: { Items: this.instruments }
     };
  }

  override configChanged(key: keyof TopBottomInstrumentsLightWidget) {
    switch (key) {
      default:
        this.refresh$.next(undefined);
        break;
    }
  }
}
